<script lang="ts">
	import type { AisleLayout, ProductData, RealBanner } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	import type { View_ProductSelection } from "proto/groceries_promotion";
	
	import { page } from "$app/stores";
	import { goto } from "$app/navigation";
	import { profile$ } from "$lib/serv/core/auth";
	import { fillLayout } from "$lib/serv/groc-product";
	import { LOCATION_PROPS } from "$lib/serv/locations";
	import { KEY_BANNER } from "$lib/ui/home/groc/Banners.svelte";
	import { KEY_OCCASION } from "$lib/ui/home/groc/Occasions.svelte";
	import { slugify } from "$lib/util/format";
	import { query } from "$lib/util/url";
	import { EMPTY, catchError, concat, delay, map, of, shareReplay, switchMap } from "rxjs";
	import { KEY_AISLE, KEY_SUB_AISLE } from "./Aisle.svelte";

	import Product from "$lib/ui/home/groc/Product.svelte";
	import Carousel from "$lib/ui/layout/Carousel.svelte";
	import Grid from "$lib/ui/layout/Grid.svelte";
	import Header from "$lib/ui/shared/Header.svelte";
	import Error from "$lib/ui/shared/Error.svelte";

	export let store: string | null;
	export let item: RealBanner;

	$: mapper = (product_selection:View_ProductSelection, sort:number) => ({
		sort,
		store_code: store,
		product_selection,
	});

	$: data$ = item.view.product_selections ? fill(item.view.product_selections) : EMPTY;

	// If the layout for the landing view is "grid", the products should be presented as one big list.
	$: gridData$ = item.view.type === "grid" ? concat(
		of({ loading: true }),
		data$.pipe(
			map((payload) => {
				const products = flattenProducts(payload.layout_items);
				if (products.length === 0) return { loading: false, error: "No products found." };

				return { loading: false, payload: products };
			}),
		)
	) : EMPTY;
	
	$: swimlanesData$ = item.view.type === "swimlanes" ? concat(
		of({
			loading: true,
			payload: {  // Make a list of bare objects for the loading skeleton; at this point we just have a list of aisle ids, subaisle ids and/or catalogue keys
				layout_items: new Array(item.view.product_selections.length)
					.fill(null)
					.map((entity, i) => ({ product_selection: item.view.product_selections[i] })) as AisleLayout[],
			},
		}),
		data$.pipe(
			map((payload) => {
				if (payload.layout_items.length > 0) return { loading: false, payload };
				
				return { loading: false, error: "No products found." };
			}),
		),
	): EMPTY;

	function fill(selection:View_ProductSelection[]) {
		const mappedSelection = selection.map(mapper);

		return profile$.pipe(
			delay(0), // wait for cache to be cleared after logout/in
			switchMap(() => fillLayout(mappedSelection).pipe(
				catchError((e) => {
					// TODO: Should we have a more refined response system depending on the error code?
					// For now, the only error that has come up is a 404, which occurs when no products
					// for this collection were found at this store.
					return of({
						layout_items: [] as AisleLayout[],
					});
				}),
			)),
			map((res) => ({
				...res,
				// only return layout items that do have products (this particular store might not have the requested subaisle, catalogue keys etc)
				layout_items: res.layout_items.filter((layout) => "products" in layout && layout.products.length),
			})),
			shareReplay(1),
		);
	}

	/*
	 * For grid views, all the products get shown as one list, not split up by aisle or subaisle.
	 * However, platform doesn't send us the data this way, so we need to do the wrangling ourselves.
	 */
	function flattenProducts(aisles: AisleLayout[]): ProductData[] {
		return aisles.reduce((flattenedProducts, aisle) => {
			return flattenedProducts.concat(aisle.products);
		}, [] as ProductData[]);
	}

	function toCacheObject(item: AisleLayout | undefined): Cache<ProductData[]> {
		return {
			loading: !item?.products?.length,
			payload: item?.products
		} as Cache<ProductData[]>;
	}

</script>

{#if item.view} <!-- This should always be defined (compulsory when creating grocery campaigns) -->
	
	<!-- `aisleId` and `subAisleId` will have values when someone clicks "See all" on one of the banner's swimlanes -->
	{@const aisleId = $page.url.searchParams.get(KEY_AISLE)}
	{@const subAisleId = $page.url.searchParams.get(KEY_SUB_AISLE)}

	{#if aisleId || subAisleId}
		<Header
			back={`/?${query($page.url).keep(...LOCATION_PROPS, 'vertical', KEY_OCCASION, KEY_BANNER).toString()}`}
			name={item.view.title ?? "collection"}
		/>
	
	{:else}
		<Header
			back={`/?${query($page.url).keep(...LOCATION_PROPS, 'vertical').toString()}`}
			image={item.view.hero_image}
		>
			{#if item.view.title}<h2 class="display-m bold">{item.view.title}</h2>{/if}
			{#if item.view.description}<p class="body-m book">{item.view.description}</p>{/if}
			{#if item.view.url}
				<div class="pills">
					<a class="label-s bold" href={item.view.url} target="_blank">
						{ item.view.url_title ?? item.view.url }
						&nbsp;<span class="icon-open-in-new-20"></span>
					</a>
				</div>
			{/if}
		</Header>
	{/if}

	{#if item.view.type === "grid"}
		<Grid cache={$gridData$} type="aisle" key={(p) => p} let:item>
			<Product {item} on:product/>
		</Grid>

	{:else if item.view.type === "swimlanes"} <!-- Could have just used an "else" as there are only two layout options currently. -->
		{@const swimlanesData = $swimlanesData$}
		{#if aisleId || subAisleId}
			{@const entity = subAisleId ? "subaisle_id" : "aisle_id"}
			{@const id = entity === "subaisle_id" ? subAisleId : aisleId}
			{@const layout = typeof swimlanesData.payload !== "undefined"
				? swimlanesData.payload.layout_items.find((layout) => layout.product_selection[entity]?.toString() === id)
				: undefined
			}

			{#if layout}
				{@const title = layout.title ?? ""}
				<Grid cache={toCacheObject(layout)} type="aisle" key={(p) => p} let:item>
					<h4 slot="title" id={slugify(title)} class="headline-m bold ellipses">{title}</h4>
					<Product {item} on:product/>
				</Grid>
			{:else}
				<Error
					title="Products not found"
					description="We could not find products belonging to this aisle."
					extra={""}
					showInput={false}
					buttonText={`Back to ${item.view.title}`}
					buttonHref={query($page.url).keep(...LOCATION_PROPS, "vertical", KEY_BANNER, KEY_OCCASION).toUrl()}
				/>
			{/if}

		{:else}
			{@const layoutItems = "payload" in swimlanesData ? swimlanesData.payload?.layout_items : undefined}
			{#if layoutItems}
				{#each layoutItems as layout}
					<Carousel cache={toCacheObject(layout)} type="aisle" key={(p) => p} let:item>
						<div slot="title" class="flex mobile flex-nowrap title">
							<h4 id={slugify(layout.title)} class="headline-m bold ellipses">{layout.title}</h4>
							<a href={`/?${query($page.url).set(
								layout.product_selection.subaisle_id ? KEY_SUB_AISLE : KEY_AISLE,
								layout.product_selection.subaisle_id ? layout.product_selection.subaisle_id : layout.product_selection.aisle_id)}`}
							class="button transparent label-xl bold underlined">See all</a>
						</div>
						<Product {item} on:product/>
					</Carousel>

				{/each}
			{:else if "error" in swimlanesData}
				<div class="message error body-m book">
					<p class="body-m book">{swimlanesData.error}</p>
				</div>
			{/if}

		{/if}
	{/if}
{/if}